import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Check, Clock, Phone, Mail, Building2, ChevronDown } from "lucide-react";
import { apiFetch } from "@/lib/api-fetch";

type DemoRequest = {
  id: number;
  name: string;
  email: string;
  company: string;
  phone: string | null;
  facility_size: string | null;
  operator_count: string | null;
  current_method: string | null;
  preferred_date: string | null;
  preferred_time: string | null;
  status: string;
  notes: string | null;
  source: string | null;
  created_at: string;
};

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending", color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
  { value: "contacted", label: "Contacted", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
  { value: "demo_scheduled", label: "Scheduled", color: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400" },
  { value: "demo_completed", label: "Completed", color: "bg-forest-100 text-forest-700 dark:bg-forest-900/30 dark:text-forest-400" },
  { value: "converted", label: "Converted", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
  { value: "lost", label: "Lost", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
];

function StatusBadge({ status }: { status: string }) {
  const opt = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${opt.color}`}>
      {opt.label}
    </span>
  );
}

export default function DemoRequestsPage() {
  const { token } = useAuth();
  const [requests, setRequests] = useState<DemoRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useEffect(() => {
    apiFetch("/api/admin/demo-requests", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => setRequests(data.requests || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  async function updateStatus(id: number, newStatus: string) {
    try {
      const res = await apiFetch(`/api/admin/demo-requests/${id}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        const data = await res.json();
        setRequests((prev) =>
          prev.map((r) => (r.id === id ? { ...r, ...data.request } : r))
        );
      }
    } catch (err) {
      console.error("Failed to update status:", err);
    }
  }

  const filtered =
    filter === "all" ? requests : requests.filter((r) => r.status === filter);

  const counts = STATUS_OPTIONS.reduce(
    (acc, s) => {
      acc[s.value] = requests.filter((r) => r.status === s.value).length;
      return acc;
    },
    {} as Record<string, number>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Demo Requests</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage and track demo request submissions
        </p>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFilter("all")}
          className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
            filter === "all"
              ? "bg-foreground text-background"
              : "bg-secondary text-muted-foreground hover:text-foreground"
          }`}
        >
          All ({requests.length})
        </button>
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.value}
            onClick={() => setFilter(s.value)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              filter === s.value
                ? "bg-foreground text-background"
                : "bg-secondary text-muted-foreground hover:text-foreground"
            }`}
          >
            {s.label} ({counts[s.value] || 0})
          </button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="bg-card border border-border rounded-xl p-8">
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-muted rounded-lg animate-pulse" />
            ))}
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <CalendarEmpty />
          <p className="text-muted-foreground mt-2">
            {filter === "all"
              ? "No demo requests yet. They'll appear here when submitted."
              : `No ${filter.replace("_", " ")} requests.`}
          </p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Contact</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Company</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Details</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((req) => (
                  <tr key={req.id} className="hover:bg-secondary/50 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-foreground">{req.name}</p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <Mail size={12} className="text-muted-foreground" />
                          <a href={`mailto:${req.email}`} className="text-xs text-muted-foreground hover:text-forest-500">
                            {req.email}
                          </a>
                        </div>
                        {req.phone && (
                          <div className="flex items-center gap-1 mt-0.5">
                            <Phone size={12} className="text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">{req.phone}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <Building2 size={14} className="text-muted-foreground" />
                        <span className="text-sm text-foreground">{req.company}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs text-muted-foreground space-y-0.5">
                        {req.facility_size && <p>Size: {req.facility_size}</p>}
                        {req.operator_count && <p>Operators: {req.operator_count}</p>}
                        {req.current_method && <p>Method: {req.current_method}</p>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={req.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs text-muted-foreground">
                        <p>{new Date(req.created_at).toLocaleDateString()}</p>
                        {req.preferred_date && (
                          <p className="mt-0.5 text-forest-500">
                            Preferred: {req.preferred_date}
                            {req.preferred_time && ` at ${req.preferred_time}`}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="relative">
                        <select
                          value={req.status}
                          onChange={(e) => updateStatus(req.id, e.target.value)}
                          className="text-xs bg-secondary border border-border rounded-lg px-2 py-1.5 text-foreground cursor-pointer focus:outline-none focus:ring-2 focus:ring-forest-600"
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s.value} value={s.value}>
                              {s.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-border">
            {filtered.map((req) => (
              <div key={req.id} className="p-4">
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedId(expandedId === req.id ? null : req.id)}
                >
                  <div>
                    <p className="text-sm font-medium text-foreground">{req.name}</p>
                    <p className="text-xs text-muted-foreground">{req.company}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={req.status} />
                    <ChevronDown
                      size={16}
                      className={`text-muted-foreground transition-transform ${
                        expandedId === req.id ? "rotate-180" : ""
                      }`}
                    />
                  </div>
                </div>
                {expandedId === req.id && (
                  <div className="mt-3 pt-3 border-t border-border space-y-2">
                    <p className="text-xs text-muted-foreground">
                      <Mail size={12} className="inline mr-1" />
                      {req.email}
                    </p>
                    {req.phone && (
                      <p className="text-xs text-muted-foreground">
                        <Phone size={12} className="inline mr-1" />
                        {req.phone}
                      </p>
                    )}
                    {req.facility_size && (
                      <p className="text-xs text-muted-foreground">Size: {req.facility_size}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Submitted: {new Date(req.created_at).toLocaleDateString()}
                    </p>
                    <select
                      value={req.status}
                      onChange={(e) => updateStatus(req.id, e.target.value)}
                      className="text-xs bg-secondary border border-border rounded-lg px-2 py-1.5 w-full text-foreground mt-2"
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CalendarEmpty() {
  return (
    <div className="w-12 h-12 mx-auto rounded-xl bg-secondary flex items-center justify-center">
      <Clock size={24} className="text-muted-foreground" />
    </div>
  );
}
